import React from 'react';
import PageHero from '../components/PageHero';
import './Volunteer.css';

const WHY = [
  { icon: '💛', bg: 'rgba(245,158,11,0.1)', title: 'Make Real Impact', desc: 'Your time directly touches lives — teaching children, supporting health camps, and mentoring women entrepreneurs in rural Tamil Nadu.' },
  { icon: '🌱', bg: 'rgba(15,118,110,0.1)', title: 'Grow as a Person', desc: 'Build leadership, communication, and project management skills through hands-on field experience you cannot learn in a classroom.' },
  { icon: '🤝', bg: 'rgba(37,99,235,0.1)', title: 'Join a Community', desc: 'Be part of a passionate network of 300+ volunteers, professionals, and changemakers who support each other beyond the field.' },
];

const ROLES = [
  {
    icon: '🎓', bg: 'rgba(15,118,110,0.1)',
    title: 'Education Volunteer',
    desc: 'Teach subjects, run digital literacy sessions, or mentor scholarship students in rural schools and after-school centres.',
    tags: ['Teaching', 'Mentoring', 'Digital Literacy'],
  },
  {
    icon: '💊', bg: 'rgba(245,158,11,0.1)',
    title: 'Healthcare Support',
    desc: 'Assist at medical camps — patient registration, health education, follow-up coordination. Medical background preferred but not required.',
    tags: ['Medical Camps', 'Health Education', 'Data Entry'],
  },
  {
    icon: '📱', bg: 'rgba(37,99,235,0.1)',
    title: 'Digital & Marketing',
    desc: 'Create content, manage social media, design creatives, or help with website updates from wherever you are.',
    tags: ['Social Media', 'Graphic Design', 'Content Writing'],
  },
  {
    icon: '👩', bg: 'rgba(124,58,237,0.08)',
    title: 'Women Empowerment',
    desc: 'Conduct skill-training workshops, help self-help groups with accounting, or run awareness sessions on legal rights.',
    tags: ['Skill Training', 'Finance Literacy', 'Legal Awareness'],
  },
  {
    icon: '🌾', bg: 'rgba(5,150,105,0.1)',
    title: 'Rural & Field Work',
    desc: 'Join field teams for water harvesting projects, village surveys, and community mobilisation in Dindigul, Erode, or Tirunelveli.',
    tags: ['Field Surveys', 'Community Work', 'Documentation'],
  },
  {
    icon: '💰', bg: 'rgba(225,29,72,0.08)',
    title: 'Fundraising & Events',
    desc: 'Organise fundraisers, run awareness events, or represent Shine People Trust at corporate CSR drives and college campaigns.',
    tags: ['Event Management', 'Fundraising', 'Public Speaking'],
  },
];

const PROCESS = [
  { num: '01', title: 'Apply Online', desc: 'Click Get Involved and fill in your details on our partner platform.' },
  { num: '02', title: 'Orientation Call', desc: "Our volunteer coordinator will call within 3 days to discuss your role and expectations." },
  { num: '03', title: 'Onboarding', desc: 'Attend a half-day onboarding session — in person (Chennai) or virtual.' },
  { num: '04', title: 'Start Volunteering', desc: "You're placed in a programme — and start making a difference from day one." },
];

const VOL_STATS = [
  { icon: '👥', num: '300+', lbl: 'Active volunteers' },
  { icon: '🏘️', num: '120+', lbl: 'Villages reached' },
  { icon: '⏱️', num: '18,000+', lbl: 'Volunteer hours / year' },
  { icon: '🌍', num: '12', lbl: 'Corporate volunteer teams' },
];

export default function Volunteer() {
  return (
    <main id="main-content">
      <PageHero
        label="Volunteer"
        title="Give Your Time, Change a Life"
        desc="Join 300+ volunteers across Tamil Nadu making a real difference — in classrooms, medical camps, farms, and communities."
        breadcrumb="Volunteer"
        bgImage="https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?auto=format&fit=crop&w=1600&h=600&q=80"
      />

      {/* Why volunteer */}
      <section className="volunteer-why section" aria-labelledby="why-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Why Volunteer With Us</span>
            <h2 className="section-title" id="why-heading">More Than Just Giving Back</h2>
            <p className="section-subtitle">Volunteering with Shine People Trust is a two-way journey — you transform communities, and communities transform you.</p>
          </div>
          <div className="why-grid" role="list">
            {WHY.map(w => (
              <div className="why-card" key={w.title} role="listitem">
                <div className="why-icon" style={{ background: w.bg }} aria-hidden="true">{w.icon}</div>
                <h3 className="why-title">{w.title}</h3>
                <p className="why-desc">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="volunteer-roles section" aria-labelledby="roles-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Find Your Role</span>
            <h2 className="section-title" id="roles-heading">Volunteer Opportunities</h2>
            <p className="section-subtitle">Whatever your skills or schedule, there is a role for you — on the ground or from your laptop.</p>
          </div>
          <div className="roles-grid" role="list">
            {ROLES.map(r => (
              <div className="role-card" key={r.title} role="listitem">
                <div className="role-icon" style={{ background: r.bg }} aria-hidden="true">{r.icon}</div>
                <div className="role-body">
                  <h3 className="role-title">{r.title}</h3>
                  <p className="role-desc">{r.desc}</p>
                  <div className="role-tags" aria-label={`Skills: ${r.tags.join(', ')}`}>
                    {r.tags.map(t => <span className="role-tag" key={t}>{t}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="volunteer-process section" aria-labelledby="process-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">How It Works</span>
            <h2 className="section-title" id="process-heading">From Application to Impact in 4 Steps</h2>
          </div>
          <div className="process-steps" role="list">
            {PROCESS.map(s => (
              <div className="process-step" key={s.num} role="listitem">
                <div className="process-step-num" aria-hidden="true">{s.num}</div>
                <h3 className="process-step-title">{s.title}</h3>
                <p className="process-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="volunteer-form-section section" aria-labelledby="vol-cta-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Ready to Start?</span>
            <h2 className="section-title" id="vol-cta-heading">Join Our Volunteer Community</h2>
            <p className="section-subtitle">300+ volunteers across Tamil Nadu are already making a difference. Your journey starts with one click.</p>
          </div>

          <div className="vol-stat-list" role="list" style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', justifyContent: 'center', marginBottom: '2.5rem' }}>
            {VOL_STATS.map(s => (
              <div className="vol-stat-item" key={s.lbl} role="listitem" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--gray-50)', borderRadius: '0.75rem', padding: '1rem 1.5rem' }}>
                <div className="vol-stat-icon" aria-hidden="true" style={{ fontSize: '1.5rem' }}>{s.icon}</div>
                <div>
                  <div className="vol-stat-num" style={{ fontWeight: 700, fontSize: '1.25rem', color: 'var(--primary)' }}>{s.num}</div>
                  <div className="vol-stat-lbl" style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{s.lbl}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center' }}>
            <a
              href="https://grantalife.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-lg"
            >
              Get Involved &rarr;
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
